import { Navigate } from "@tanstack/react-router";
import { useAuth } from "@/hooks/useAuth";
import { useActiveLocation } from "@/contexts/ActiveLocationContext";
import { WorkingPageHeader } from "./WorkingPageHeader";
import { WorkingStatsBar } from "./WorkingStatsBar";
import { PrepCapacityCheck } from "./PrepCapacityCheck";
import { WorkingAlerts } from "./WorkingAlerts";
import { CookingNowSection } from "./CookingNowSection";
import { OnDeckSection } from "./OnDeckSection";
import { CompletedSection } from "./CompletedSection";
import { useCookingSessionStats } from "@/hooks/cooking-sessions/queries";
import { useIsTablet } from "@/hooks/use-mobile";
import { PageHeader } from "@/components/layout/PageHeaderSlot";
import { MultiViewHint } from "./multi-view/MultiViewHint";
import { MultiViewSelectionBar } from "./multi-view/MultiViewSelectionBar";
import { useMultiViewSelectionLifecycle } from "./multi-view/useMultiViewSelectionLifecycle";
import type { CookingSessionSource } from "@/types/cooking-session";

interface WorkingBoardProps {
  /** Prep, or the tickets. */
  source: CookingSessionSource;
  /** Where an unauthorised user is told they came from. */
  from: string;
}

/**
 * One board, twice (T-1105).
 *
 * Prep and service ran through a single list, so a recipe queued for tomorrow
 * morning sat between live tickets in the middle of service — "you're not gonna
 * want that overlap when you're running service". The two boards are the same
 * lanes over different work, and `orderId` already separates them: prep belongs
 * to no ticket. Everything below takes `source` and passes it to the server, so
 * counts, capacity and pagination all describe the board you are looking at
 * rather than both at once.
 */
export function WorkingBoard({ source, from }: WorkingBoardProps) {
  const { hasPermission, isLoading } = useAuth();
  const { activeLocation } = useActiveLocation();
  const isTablet = useIsTablet();
  const locationId = activeLocation?.id;
  const canReadCookingSessions = hasPermission("cooking-session:read");
  const { data: stats } = useCookingSessionStats(
    { locationId, source },
    { enabled: canReadCookingSessions && !isLoading },
  );
  const hasActiveSessions = (stats?.totalActive ?? 0) > 0;
  // Multi View is opened from Prep: the hint, the chips and the ticks that outlive this page.
  const multiView = source === "prep";
  useMultiViewSelectionLifecycle();

  if (!isLoading && !canReadCookingSessions) {
    return (
      <Navigate
        to="/unauthorized"
        search={{
          reason: "permission_denied",
          from,
          requiredPermission: "cooking-session:read",
        }}
        replace
      />
    );
  }

  return (
    <div className="flex flex-col gap-0 overflow-y-auto">
      {isTablet ? (
        <PageHeader>
          <WorkingPageHeader tablet source={source} />
        </PageHeader>
      ) : (
        <WorkingPageHeader source={source} />
      )}
      <WorkingStatsBar locationId={locationId} source={source} />
      {hasActiveSessions && (
        <div className="mx-3 mb-3 flex gap-3">
          <PrepCapacityCheck locationId={locationId} source={source} />
          <WorkingAlerts locationId={locationId} source={source} />
        </div>
      )}
      <div className="flex flex-col gap-6 pb-6">
        {multiView && <MultiViewHint />}
        <CookingNowSection locationId={locationId} source={source} />
        <OnDeckSection locationId={locationId} source={source} />
        <CompletedSection locationId={locationId} source={source} />
      </div>
      {multiView && <MultiViewSelectionBar />}
    </div>
  );
}
